import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, X, Pill, Minus, Plus, Trash2, User, Upload, Send, Loader2, Phone, MapPin, Truck, Store, MessageCircle } from 'lucide-react';

const CartDrawer = ({ isOpen, onClose, cart, updateQuantity, removeFromCart, cartTotal, lang }) => {
  const [showPayment, setShowPayment] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [pharmacyWhatsapp, setPharmacyWhatsapp] = useState(null); 
  const [customerName, setCustomerName] = useState('');
  
  const [deliveryType, setDeliveryType] = useState('pickup'); 
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');

  const [receiptFile, setReceiptFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  const BASE_API_URL = "https://my-pharmacy-api.onrender.com";

  useEffect(() => {
    if (isOpen) {
      axios.get(`${BASE_API_URL}/api/settings/qr`)
        .then(res => {
          if (res.data) {
            // التعديل: استلام رابط Cloudinary المباشر للـ QR
            if (res.data.qr_image_url) setQrCodeUrl(res.data.qr_image_url);
            if (res.data.whatsapp_number) setPharmacyWhatsapp(res.data.whatsapp_number);
          }
        })
        .catch(err => console.error("❌ فشل جلب البيانات:", err));
    }
  }, [isOpen]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setReceiptFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const confirmPayment = async () => {
    if (!customerName.trim()) { alert(lang === 'ar' ? "يرجى إدخل الاسم" : "Please enter name"); return; }
    
    if (deliveryType === 'delivery' && !phone.trim()) { 
        alert(lang === 'ar' ? "يرجى إدخال رقم هاتفك للتواصل" : "Please enter your phone number"); return; 
    }
    
    if (deliveryType === 'delivery' && !address.trim()) { alert(lang === 'ar' ? "يرجى إدخال عنوان التوصيل" : "Please enter address"); return; }
    if (!receiptFile) { alert(lang === 'ar' ? "يرجى رفع صورة إيصال الدفع" : "Please upload receipt"); return; }

    setLoading(true);
    const orderDetails = cart.map(item => ({ id: item.id, name: item.name, quantity: item.quantity }));
    const formData = new FormData();
    formData.append('customerName', customerName);
    formData.append('totalPrice', cartTotal);
    formData.append('details', JSON.stringify(orderDetails));
    formData.append('receipt', receiptFile);
    formData.append('deliveryType', deliveryType);
    
    formData.append('phone', deliveryType === 'pickup' ? (pharmacyWhatsapp || 'N/A') : phone);
    formData.append('address', deliveryType === 'delivery' ? address : 'استلام من الصيدلية');

    try {
      const res = await axios.post(`${BASE_API_URL}/api/orders`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.status === 200 || res.status === 201) {
        alert(lang === 'ar' ? "✅ تم بنجاح! شكراً لثقتكم." : "✅ Success! Thank you.");
        window.location.reload(); 
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Error";
      alert(lang === 'ar' ? `❌ خطأ: ${errorMsg}` : `❌ Error: ${errorMsg}`);
    } finally { setLoading(false); }
  };

  const t = {
    ar: { 
      cartTitle: 'حقيبة المشتريات', total: 'المجموع الإجمالي', checkout: 'إتمام الطلب الآن', 
      empty: 'السلة فارغة', payTitle: 'تأكيد الطلب والدفع', 
      namePlaceholder: 'الاسم الكامل', phonePlaceholder: 'رقم هاتف التواصل الخاص بك', addressPlaceholder: ' يوجد اجور شحن +عنوان التوصيل (الحي - الشارع)',
      uploadText: 'ارفع صورة الإيصال هنا', pickup: 'استلام من الصيدلية', delivery: 'توصيل منزلي',
      sending: 'جاري الإرسال...', qrLoading: 'جاري تحميل الرمز...',
      pharmacyContact: 'رقم واتساب الصيدلية'
    },
    en: { 
      cartTitle: 'Shopping Cart', total: 'Total Amount', checkout: 'Checkout Now', 
      empty: 'Empty Cart', payTitle: 'Confirm Order & Pay', 
      namePlaceholder: 'Full Name', phonePlaceholder: 'Your Phone Number', addressPlaceholder: ' Delivery Address',
      uploadText: 'Upload receipt', pickup: 'In-Store Pickup', delivery: 'Home Delivery',
      sending: 'Sending...', qrLoading: 'Loading QR...',
      pharmacyContact: 'Pharmacy WhatsApp'
    }
  }[lang];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-md z-[100]" />
          <motion.div 
            initial={{ x: lang === 'ar' ? '-100%' : '100%' }} animate={{ x: 0 }} exit={{ x: lang === 'ar' ? '-100%' : '100%' }} 
            className={`fixed top-0 bottom-0 w-full max-w-md bg-white dark:bg-[#0D1117] z-[101] shadow-2xl p-5 flex flex-col ${lang === 'ar' ? 'left-0' : 'right-0'}`}
          >
            {!showPayment ? (
              <>
                <div className="flex justify-between items-center mb-6 border-b pb-4 dark:border-gray-800">
                  <h2 className="text-xl font-black dark:text-white flex items-center gap-2">
                    <ShoppingBag className="text-pink-500" /> {t.cartTitle}
                  </h2>
                  <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors">
                    <X size={24} className="text-gray-400" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto space-y-3 no-scrollbar px-1">
                  {cart.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full opacity-30">
                      <Pill size={64} className="mb-4 text-gray-400" />
                      <p className="font-bold text-lg text-gray-400">{t.empty}</p>
                    </div>
                  ) : (
                    cart.map((item) => (
                      <div key={item.id} className="flex gap-4 bg-gray-50 dark:bg-gray-900/50 p-4 rounded-2xl border border-gray-100 dark:border-gray-800">
                         {/* التعديل: عرض صورة المنتج مباشرة من Cloudinary */}
                         <img src={item.image_url.startsWith('http') ? item.image_url : `${BASE_API_URL}${item.image_url}`} className="w-16 h-16 object-contain rounded-xl bg-white p-1" alt="" />
                         <div className="flex-1">
                            <h4 className="font-bold dark:text-white text-sm">{item.name}</h4>
                            <p className="text-pink-600 font-black text-sm mt-1">{item.price_syp.toLocaleString()} ل.س</p>
                            <div className="flex items-center justify-between mt-2">
                               <div className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 px-2">
                                  <button onClick={() => updateQuantity(item.id, -1)} className="text-gray-500 py-1"><Minus size={14}/></button>
                                  <span className="font-bold dark:text-white text-sm min-w-[20px] text-center">{item.quantity}</span>
                                  <button onClick={() => updateQuantity(item.id, 1)} className="text-gray-500 py-1"><Plus size={14}/></button>
                               </div>
                               <button onClick={() => removeFromCart(item.id)} className="text-red-400 p-1"><Trash2 size={18}/></button>
                            </div>
                         </div>
                      </div>
                    ))
                  )}
                </div>

                {cart.length > 0 && (
                  <div className="mt-6 pt-6 border-t dark:border-gray-800">
                    <div className="flex justify-between items-center mb-4 px-2">
                      <span className="text-gray-500 font-bold">{t.total}</span>
                      <span className="text-2xl font-black text-pink-600">{cartTotal.toLocaleString()} ل.س</span>
                    </div>
                    <button onClick={() => setShowPayment(true)} className="w-full bg-gradient-to-r from-pink-500 to-indigo-600 text-white py-4 rounded-2xl font-black shadow-lg active:scale-95 transition-all">
                        {t.checkout}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col h-full overflow-y-auto no-scrollbar">
                <div className="flex items-center justify-between mb-6">
                  <button onClick={() => setShowPayment(false)} className="p-2 bg-gray-100 dark:bg-gray-800 rounded-full"><X size={20} className="dark:text-white"/></button>
                  <span className="font-black text-lg dark:text-white">{t.payTitle}</span>
                  <div className="w-10"></div>
                </div>

                <div className="flex gap-2 p-1 bg-gray-100 dark:bg-gray-800 rounded-2xl mb-4">
                  <button onClick={() => setDeliveryType('pickup')} className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-black text-xs transition-all ${deliveryType === 'pickup' ? 'bg-white dark:bg-indigo-600 shadow-sm dark:text-white' : 'text-gray-500'}`}><Store size={16} /> {t.pickup}</button>
                  <button onClick={() => setDeliveryType('delivery')} className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-black text-xs transition-all ${deliveryType === 'delivery' ? 'bg-white dark:bg-indigo-600 shadow-sm dark:text-white' : 'text-gray-500'}`}><Truck size={16} /> {t.delivery}</button>
                </div>

                <div className="space-y-4">
                  <div className="w-full bg-gray-50 dark:bg-gray-900 p-4 rounded-2xl border dark:border-gray-800 flex items-center gap-3">
                    <User className="text-indigo-500" size={20} />
                    <input type="text" placeholder={t.namePlaceholder} className="bg-transparent w-full outline-none font-bold dark:text-white text-sm" onChange={(e) => setCustomerName(e.target.value)} />
                  </div>

                  {deliveryType === 'pickup' ? (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-4 bg-green-500/10 border-2 border-green-500/20 rounded-2xl flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <MessageCircle className="text-green-500" size={20} />
                        <div>
                          <p className="text-[10px] font-black text-green-600 uppercase">{t.pharmacyContact}</p>
                          <p className="font-black text-sm dark:text-white">{pharmacyWhatsapp || '---'}</p>
                        </div>
                      </div>
                      <a href={`https://wa.me/${pharmacyWhatsapp?.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="bg-green-500 p-2 rounded-xl text-white shadow-lg active:scale-90 transition-all">
                        <Phone size={16} />
                      </a>
                    </motion.div>
                  ) : (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-full bg-gray-50 dark:bg-gray-900 p-4 rounded-2xl border dark:border-gray-800 flex items-center gap-3">
                      <Phone className="text-green-500" size={20} />
                      <input type="tel" placeholder={t.phonePlaceholder} className="bg-transparent w-full outline-none font-bold dark:text-white text-sm" onChange={(e) => setPhone(e.target.value)} />
                    </motion.div>
                  )}

                  <AnimatePresence>
                    {deliveryType === 'delivery' && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="w-full bg-gray-50 dark:bg-gray-900 p-4 rounded-2xl border dark:border-gray-800 flex items-center gap-3">
                        <MapPin className="text-red-500" size={20} />
                        <input type="text" placeholder={t.addressPlaceholder} className="bg-transparent w-full outline-none font-bold dark:text-white text-sm" onChange={(e) => setAddress(e.target.value)} />
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="bg-white p-4 rounded-3xl shadow-inner border border-gray-100 flex flex-col items-center">
                    {/* التعديل: عرض رمز QR مباشرة من Cloudinary */}
                    {qrCodeUrl ? (
                      <img src={qrCodeUrl.startsWith('http') ? qrCodeUrl : `${BASE_API_URL}${qrCodeUrl}`} className="w-40 h-40 object-contain" alt="Payment QR" />
                    ) : (
                      <div className="w-40 h-40 flex items-center justify-center text-gray-400 animate-pulse bg-gray-50 rounded-xl text-xs">{t.qrLoading}</div>
                    )}
                    <p className="mt-2 text-[9px] text-gray-400 font-bold uppercase text-center">{lang === 'ar' ? 'ادفع المبلغ ثم ارفع صورة الإيصال بالأسفل' : 'Pay then upload receipt below'}</p>
                  </div>

                  <div className="relative group">
                     <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-3xl cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-all overflow-hidden">
                        {previewUrl ? <img src={previewUrl} className="w-full h-full object-cover" alt="Preview" /> : <div className="flex flex-col items-center text-gray-400"><Upload size={24} className="mb-2" /><span className="text-[10px] font-bold">{t.uploadText}</span></div>}
                        <input type="file" className="hidden" accept="image/*" onChange={handleFileChange} />
                     </label>
                  </div>
                </div>

                <div className="mt-auto pt-6 space-y-3">
                  <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-2xl flex justify-between items-center">
                    <span className="text-[10px] font-black text-indigo-400 uppercase tracking-wider">Amount Due</span>
                    <span className="font-black text-indigo-600 dark:text-indigo-400 text-lg">{cartTotal.toLocaleString()} ل.س</span>
                  </div>
                  <button onClick={confirmPayment} disabled={loading} className={`w-full py-4 rounded-2xl font-black flex items-center justify-center gap-2 shadow-xl transition-all ${loading ? 'bg-gray-300 cursor-not-allowed' : 'bg-gray-900 dark:bg-indigo-600 text-white active:scale-95'}`}>
                    {loading ? <Loader2 className="animate-spin" size={20} /> : <Send size={20} />}
                    {loading ? t.sending : (lang === 'ar' ? 'إرسال الطلب الآن' : 'Send Order')}
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;